"use client";

import Link from "next/link";
import { IconClose } from "@/app/components/atoms/Icons";
import { cx } from "@/app/lib/utils";
import styles from "./Chip.module.scss";
import type { ChipProps } from "./types";

/**
 * Chip — pastilla interactiva del DS Entradas.
 *
 * Forma pill (`--border-radius-full`) y tipografía Label/S. Se renderiza como
 * `<button>` con estado `aria-pressed` para reflejar la selección — salvo en
 * la variante `removable`, donde el elemento interactivo pasa a ser solo su
 * botón de cierre (ver `types.ts`).
 *
 * Client Component: gestiona interacción (`onClick`/`onRemove`) y estado de
 * selección.
 */
export function Chip({
  label,
  selected = false,
  leadingIcon,
  onClick,
  disabled = false,
  inverse = false,
  removable = false,
  onRemove,
  href,
  prefetch,
  className,
}: ChipProps) {
  const chipClassName = cx(
    styles.chip,
    selected ? styles.selected : styles.unselected,
    inverse && styles.inverse,
    className,
  );
  const content = (
    <>
      {leadingIcon && (
        <span className={styles.icon} aria-hidden="true">
          {leadingIcon}
        </span>
      )}
      <span>{label}</span>
    </>
  );

  if (href) {
    return (
      <Link href={href} prefetch={prefetch} className={chipClassName}>
        {content}
      </Link>
    );
  }

  if (removable) {
    return (
      <span className={chipClassName}>
        {content}
        <button
          type="button"
          className={styles.remove}
          onClick={onRemove}
          disabled={disabled}
          aria-label={`Quitar filtro: ${label}`}
        >
          <IconClose size={16} />
        </button>
      </span>
    );
  }

  return (
    <button
      type="button"
      className={chipClassName}
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
    >
      {content}
    </button>
  );
}
